import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import { Subscription } from 'rxjs'
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { VenueColourDirective } from '../directives/venue-colour.directive'
import { Menu, MenuItem, Consumable } from '../domain/Consumable'
import { ProductLibrary } from '../domain/ProductLibrary'
import { Venue } from '../domain/Venue'
import { BookingDocket, CoverMenuChoice, CoverProductSelection } from '../domain/BookingDocket'
import { Modifier, ModifierGroup } from '../domain/Modifier'
import { BookingService } from '../services/booking.service'
import { ToastService } from '../services/toast.service'
import { Toast, ToastType } from '../domain/Toast'
import { courseLimitsDisplay } from './course-limits-display'

@Component({
    selector: 'app-cover-choices-edit',
    standalone: true,
    imports: [ReactiveFormsModule, VenueColourDirective],
    templateUrl: './cover-choices-edit.component.html',
    styleUrls: ['./menu-workspace.sass', './cover-choices-edit.component.sass'],
})
export class CoverChoicesEditComponent implements OnInit, OnDestroy {
    @Input() venue!: Venue
    @Input() menu!: Menu
    @Input() coverIndex!: number
    @Input() productLibrary!: ProductLibrary
    @Input() bookingId: string | null = null
    @Input() docket: BookingDocket | null = null
    @Input() partySize: number | null = null
    @Input() prefilledName: string | null = null
    @Input() canEditAfterSubmit = true
    @Output() saved = new EventEmitter<void>()
    @Output() cancelled = new EventEmitter<void>()
    @ViewChildren('courseSection', { read: ElementRef }) courseSections!: QueryList<ElementRef<HTMLElement>>
    @ViewChild('reviewSummary') reviewSummary!: TemplateRef<unknown>
    @ViewChild('discardChanges') discardChanges!: TemplateRef<unknown>

    items!: {
        courseId: string,
        courseName: string,
        menuItem: MenuItem,
        consumable: Consumable,
        descriptionFull: string | null,
        descriptionShort: string | null,
        canTruncate: boolean,
        expandedControlName: string,
        modifierGroups: Array<{
            id: string,
            name: string,
            minimumModifiersRequired: number | null,
            maximumModifiersRequired: number | null,
            modifiers: Array<{
                modifier: Modifier,
                primarySkuId: string | null
            }>
        }>
    }[]
    characterLimit = 140
    form!: FormGroup
    saving = false
    submitted = false
    private reviewModal?: NgbModalRef
    private initialChoice = ''
    private leaveConfirmation?: Promise<boolean>
    private selectionChanges?: Subscription

    constructor(
        private fb: FormBuilder,
        private bookingService: BookingService,
        private toastService: ToastService,
        private modalService: NgbModal
    ) {}

    ngOnInit() {
        this.items = this.menu.menuCourses.flatMap(course => 
            course.menuItems.flatMap(menuItem => {
                const consumable = this.productLibrary.getConsumable(menuItem.consumableId)
                if (!consumable?.getSKUWithId(menuItem.skuId)?.onSale) {
                    return []
                }
                const canTruncate = !!consumable.description && consumable.description.length > this.characterLimit
                return {
                    courseId: course.id,
                    courseName: course.displayName,
                    menuItem: menuItem,
                    consumable,
                    descriptionFull: consumable.description,
                    descriptionShort: consumable.description ? consumable.description.slice(0, this.characterLimit) : null,
                    canTruncate,
                    expandedControlName: menuItem.id,
                    modifierGroups: consumable.modifierGroupIds
                        .map(id => this.productLibrary.getModifierGroup(id))
                        .filter((g): g is ModifierGroup => !!g)
                        .map(modifierGroup => ({
                            id: modifierGroup.id,
                            name: modifierGroup.name,
                            minimumModifiersRequired: modifierGroup.minimumModifiersRequired,
                            maximumModifiersRequired: modifierGroup.maximumModifiersRequired,
                            modifiers: modifierGroup.modifiers.map(modifier => ({
                                modifier: modifier,
                                primarySkuId: modifier.skus[0]!.id
                            }))
                        }))
                }
            })
        )
        this.buildFormFromDocketCoverChoices()
        this.selectionChanges?.unsubscribe()
        this.selectionChanges = this.form.valueChanges.subscribe(() => this.updateSelectionAvailability())
        this.updateSelectionAvailability()
        this.initialChoice = JSON.stringify(this.currentChoice)
    }

    ngOnDestroy() {
        this.selectionChanges?.unsubscribe()
    }

    dismiss() {
        this.cancelled.emit()
    }

    private get currentChoice(): CoverMenuChoice {
        return new CoverMenuChoice(this.form.value.name ?? null, this.form.value.notes ?? null, this.buildSelections())
    }

    get hasUnsavedChanges(): boolean {
        return !!this.form && this.initialChoice !== JSON.stringify(this.currentChoice)
    }

    canLeave(): boolean | Promise<boolean> {
        if (this.saving) return false
        this.reviewModal?.dismiss()
        if (!this.hasUnsavedChanges) return true
        return this.leaveConfirmation ??= this.modalService.open(this.discardChanges, {
            centered: true, ariaLabelledBy: 'discard-menu-title',
        }).result.then(result => result === true, () => false).finally(() => this.leaveConfirmation = undefined)
    }

    private buildFormFromDocketCoverChoices() {
        const existingChoice = this.docket?.coverChoices?.[this.coverIndex] || new CoverMenuChoice(null, null, [])
        const nameValue = this.prefilledName || existingChoice.name
        
        this.form = this.fb.group({
            name: new FormControl<string | null>(nameValue),
            notes: new FormControl<string | null>(existingChoice.notes),
            productSelection: this.fb.group({}),
            modifierSelection: this.fb.group({}),
            ui: this.fb.group({
                expandedDescriptions: this.fb.group({
                    ...this.items.map(it => it.expandedControlName)
                        .reduce((acc, cur) => ({ ...acc, [cur]: new FormControl<boolean>(false, { nonNullable: true }) }), {})
                })
            })
        })
        const productSelectionGroup = this.form.get('productSelection') as FormGroup
        const modifierSelectionGroup = this.form.get('modifierSelection') as FormGroup
        const chosenByItem = new Map(existingChoice.selections.flatMap(selection => {
            const item = this.menu.findItemForSelection(selection)
            return item ? [[item.id, selection] as const] : []
        }))
        for (const menuItem of this.items) {
            productSelectionGroup.addControl(menuItem.menuItem.id, new FormControl<boolean>(chosenByItem.has(menuItem.menuItem.id), { nonNullable: true }))
            const modifierSkuIdFormGroup = new FormGroup({})
            const selectedModifierSkuIds = new Set(chosenByItem.get(menuItem.menuItem.id)?.modifierSkuIds ?? [])
            for (const group of menuItem.modifierGroups) {
                for (const { primarySkuId: modifierSkuId } of group.modifiers) {
                    if (!modifierSkuId) {
                        continue
                    }
                    modifierSkuIdFormGroup.addControl(
                        modifierSkuId, 
                        new FormControl<boolean>(selectedModifierSkuIds.has(modifierSkuId), { nonNullable: true })
                    )
                }
            }
            modifierSelectionGroup.addControl(menuItem.menuItem.id, modifierSkuIdFormGroup)
        }
    }

    private buildSelections(): CoverProductSelection[] {
        const productSelectionGroup = this.form.get('productSelection') as FormGroup
        const modifierSelectionGroup = this.form.get('modifierSelection') as FormGroup
        const selectedProducts: string[] = Object.entries(productSelectionGroup.value)
            .filter(([, v]) => !!v)
            .map(([k]) => k)
        const next: CoverProductSelection[] = []
        for (const menuItemId of selectedProducts) {
            const modifierGroup = modifierSelectionGroup.get(menuItemId) as FormGroup | null
            let modifierSkuIds: string[] = []
            if (modifierGroup) {
                modifierSkuIds = Object.entries(modifierGroup.value)
                    .filter(([, v]) => !!v)
                    .map(([k]) => k)
            }
            const item = this.items.find(item => item.menuItem.id === menuItemId)!
            next.push(new CoverProductSelection(item.menuItem.skuId, modifierSkuIds, menuItemId))
        }
        return next
    }

    toggleDescription(menuItemId: string) {
        const control = this.form.get(['ui', 'expandedDescriptions', menuItemId].join('.')) as FormControl<boolean> | null
        if (!control) { return }
        control.setValue(!control.value)
        control.markAsDirty()
        control.markAsTouched()
    }

    scrollToCourse(courseId: string) {
        const section = this.courseSections.find(ref => ref.nativeElement.dataset['courseId'] === courseId)?.nativeElement
        section?.scrollIntoView({ block: 'start' })
        section?.focus({ preventScroll: true })
    }

    removeProduct(menuItemId: string) {
        const control = this.form.get(['productSelection', menuItemId])
        control?.setValue(false)
        control?.markAsDirty()
    }

    get selectedCourseCount(): number {
        return this.menu.countSelectedCourses(this.buildSelections().map(selection => selection.menuItemId!))
    }

    get menuCourseLimitsDisplay(): string | null {
        const limits = this.effectiveCourseLimits
        return courseLimitsDisplay(limits.minimumCoursesRequired, limits.maximumCoursesRequired)
    }

    get hasCourseLimits(): boolean {
        const limits = this.effectiveCourseLimits
        return limits.minimumCoursesRequired !== null || limits.maximumCoursesRequired !== null
    }

    private get effectiveCourseLimits() {
        return this.docket?.getCourseLimits(this.menu) ?? this.menu
    }

    get menuCourseValidationError(): string | null {
        const selectedCount = this.selectedCourseCount
        const limits = this.effectiveCourseLimits
        const minimum = limits.minimumCoursesRequired
        const maximum = limits.maximumCoursesRequired
        if (minimum !== null && selectedCount < minimum) {
            return `Please select at least ${minimum} ${minimum === 1 ? 'course' : 'courses'} (currently ${selectedCount} selected)`
        }
        if (maximum !== null && selectedCount > maximum) {
            return `Please select no more than ${maximum} ${maximum === 1 ? 'course' : 'courses'} (currently ${selectedCount} selected)`
        }
        return null
    }

    get showMenuCourseValidationError(): boolean {
        const maximum = this.effectiveCourseLimits.maximumCoursesRequired
        return this.submitted || (this.form.get('productSelection')!.dirty && maximum !== null && this.selectedCourseCount > maximum)
    }

    get modifierSelectionGroup(): FormGroup | null {
        return this.form?.get('modifierSelection') as FormGroup | null
    }

    get itemsByCourse(): Array<{ courseId: string, courseName: string, courseDescription: string | null, items: CoverChoicesEditComponent['items'] }> {
        const courseMap = new Map<string, CoverChoicesEditComponent['items']>()
        for (const item of this.items) {
            if (!courseMap.has(item.courseId)) {
                courseMap.set(item.courseId, [])
            }
            courseMap.get(item.courseId)!.push(item)
        }
        
        // Sort courses by their first item's menu course order
        const courses = Array.from(courseMap.entries()).map(([courseId, items]) => ({
            courseId,
            courseName: items[0].courseName,
            courseDescription: this.menu.menuCourses.find(course => course.id === courseId)?.description ?? null,
            items
        }))
        
        return courses
    }

    get selectedItems() {
        return this.items
            .filter(item => this.isProductSelected(item.menuItem.id))
            .map(item => ({
                courseName: item.courseName,
                productName: item.consumable.displayName,
                menuItemId: item.menuItem.id,
                extra: this.isCourseExcludedFromCount(item.courseId),
                modifiers: item.modifierGroups.map(group => ({
                    name: group.name,
                    choices: group.modifiers.filter(modifier => modifier.primarySkuId && this.form.get(['modifierSelection', item.menuItem.id, modifier.primarySkuId])?.value)
                        .map(modifier => modifier.modifier.displayName),
                })).filter(group => group.choices.length > 0),
            }))
    }

    getSelectedCountForCourse(courseId: string): number {
        return this.items
            .filter(item => item.courseId === courseId && this.isProductSelected(item.menuItem.id))
            .length
    }

    isProductDisabled(menuItemId: string, courseId: string): boolean {
        if (this.isProductSelected(menuItemId)) return false
        const maximum = this.menu.menuCourses.find(course => course.id === courseId)?.maximumItemsRequired
        return maximum != null && this.getSelectedCountForCourse(courseId) >= maximum
    }

    isModifierDisabled(menuItemId: string, group: CoverChoicesEditComponent['items'][number]['modifierGroups'][number], skuId: string): boolean {
        const selections = this.form.get(['modifierSelection', menuItemId]) as FormGroup
        if (selections.get(skuId)?.value || group.maximumModifiersRequired === null) return false
        return group.modifiers.filter(modifier => modifier.primarySkuId && selections.get(modifier.primarySkuId)?.value).length >= group.maximumModifiersRequired
    }

    private updateSelectionAvailability() {
        const setDisabled = (control: FormControl, disabled: boolean) => {
            if (control.disabled === disabled) return
            if (disabled) control.disable({ emitEvent: false })
            else control.enable({ emitEvent: false })
        }
        for (const item of this.items) {
            setDisabled(this.form.get(['productSelection', item.menuItem.id]) as FormControl, this.isProductDisabled(item.menuItem.id, item.courseId))
            for (const group of item.modifierGroups) {
                for (const modifier of group.modifiers) {
                    if (modifier.primarySkuId) {
                        setDisabled(this.form.get(['modifierSelection', item.menuItem.id, modifier.primarySkuId]) as FormControl, this.isModifierDisabled(item.menuItem.id, group, modifier.primarySkuId))
                    }
                }
            }
        }
    }

    isCourseComplete(courseId: string): boolean {
        const selectedItems = this.items.filter(item => item.courseId === courseId && this.isProductSelected(item.menuItem.id))
        return selectedItems.length > 0 && !this.getCourseValidationError(courseId, true)
            && selectedItems.every(item => item.modifierGroups.every(group => !this.getModifierGroupValidationError(item.menuItem.id, group, true)))
    }

    isCourseExcludedFromCount(courseId: string): boolean {
        return this.menu.menuCourses.find(course => course.id === courseId)?.excludesFromCourseCount ?? false
    }

    getCourseLimitsDisplay(courseId: string): string | null {
        const course = this.menu.menuCourses.find(c => c.id === courseId)
        if (!course) return null
        
        const min = course.minimumItemsRequired
        const max = course.maximumItemsRequired
        
        if (min !== null && max !== null) {
            if (min === max) {
                return `Select exactly ${min} ${min === 1 ? 'item' : 'items'}`
            }
            return `Select ${min}-${max} items`
        } else if (min !== null) {
            return `Select at least ${min} ${min === 1 ? 'item' : 'items'}`
        } else if (max !== null) {
            return `Select up to ${max} ${max === 1 ? 'item' : 'items'}`
        }
        return null
    }

    getModifierGroupLimitsDisplay(modifierGroup: { minimumModifiersRequired: number | null, maximumModifiersRequired: number | null }): string | null {
        const min = modifierGroup.minimumModifiersRequired
        const max = modifierGroup.maximumModifiersRequired
        
        if (min !== null && max !== null) {
            if (min === max) {
                return `Select exactly ${min} ${min === 1 ? 'option' : 'options'}`
            }
            return `Select ${min}-${max} options`
        } else if (min !== null) {
            return `Select at least ${min} ${min === 1 ? 'option' : 'options'}`
        } else if (max !== null) {
            return `Select up to ${max} ${max === 1 ? 'option' : 'options'}`
        }
        return null
    }

    getCourseValidationError(courseId: string, includePristine = false): string | null {
        const course = this.menu.menuCourses.find(c => c.id === courseId)
        if (!course) return null
        
        const productSelectionGroup = this.form.get('productSelection') as FormGroup
        if (productSelectionGroup.pristine && !this.submitted && !includePristine) return null
        
        const selectedCount = this.getSelectedCountForCourse(courseId)
        if (this.hasCourseLimits && selectedCount === 0) return null
        const min = course.minimumItemsRequired
        const max = course.maximumItemsRequired
        
        if (min !== null && selectedCount < min) {
            return `Please select at least ${min} ${min === 1 ? 'item' : 'items'}`
        }
        if (max !== null && selectedCount > max) {
            return `Please select no more than ${max} ${max === 1 ? 'item' : 'items'}`
        }
        
        return null
    }

    getModifierGroupValidationError(menuItemId: string, modifierGroup: { id: string, minimumModifiersRequired: number | null, maximumModifiersRequired: number | null, modifiers: Array<{ primarySkuId: string | null }> }, includePristine = false): string | null {
        if (!this.isProductSelected(menuItemId)) {
            return null
        }
        const min = modifierGroup.minimumModifiersRequired
        const max = modifierGroup.maximumModifiersRequired
        if (min === null && max === null) {
            return null
        }
        const modifierSelectionGroup = this.form.get('modifierSelection') as FormGroup
        const modifierGroupForm = modifierSelectionGroup.get(menuItemId) as FormGroup | null
        if (!modifierGroupForm || (modifierGroupForm.pristine && !this.submitted && !includePristine)) {
            return null
        }
        const modifierSkuIds = modifierGroup.modifiers.map(m => m.primarySkuId).filter(id => id !== null) as string[]
        const selectedCount = modifierSkuIds.filter(skuId => {
            const control = modifierGroupForm.get(skuId)
            return control?.value === true
        }).length
        if (min !== null && selectedCount < min) {
            return `Please select at least ${min} ${min === 1 ? 'option' : 'options'}`
        }
        if (max !== null && selectedCount > max) {
            return `Please select no more than ${max} ${max === 1 ? 'option' : 'options'}`
        }
        return null
    }

    validateCourseLimits(): { valid: boolean, errors: string[] } {
        const errors: string[] = []
        const menuError = this.menuCourseValidationError
        if (menuError) {
            errors.push(menuError)
        }
        
        for (const course of this.menu.menuCourses) {
            const selectedCount = this.getSelectedCountForCourse(course.id)
            if (this.hasCourseLimits && selectedCount === 0) continue
            const min = course.minimumItemsRequired
            const max = course.maximumItemsRequired
            
            if (min !== null && selectedCount < min) {
                errors.push(`${course.displayName}: Please select at least ${min} ${min === 1 ? 'item' : 'items'} (currently ${selectedCount} selected)`)
            }
            if (max !== null && selectedCount > max) {
                errors.push(`${course.displayName}: Please select no more than ${max} ${max === 1 ? 'item' : 'items'} (currently ${selectedCount} selected)`)
            }
        }
        
        return { valid: errors.length === 0, errors }
    }

    validateModifierLimits(): { valid: boolean, errors: string[] } {
        const errors: string[] = []
        const productSelectionGroup = this.form.get('productSelection') as FormGroup
        const modifierSelectionGroup = this.form.get('modifierSelection') as FormGroup
        
        // Check each selected product's modifiers
        const selectedProducts = Object.entries(productSelectionGroup.value)
            .filter(([, v]) => !!v)
            .map(([k]) => k)
        
        for (const menuItemId of selectedProducts) {
            const item = this.items.find(i => i.menuItem.id === menuItemId)
            if (!item) continue
            
            // Check each modifier group for this product
            for (const modifierGroup of item.modifierGroups) {
                const min = modifierGroup.minimumModifiersRequired
                const max = modifierGroup.maximumModifiersRequired
                
                if (min === null && max === null) continue
                
                // Count selected modifiers in this group
                const modifierSkuIds = modifierGroup.modifiers.map(m => m.primarySkuId).filter(id => id !== null) as string[]
                const modifierGroupForm = modifierSelectionGroup.get(menuItemId) as FormGroup | null
                if (!modifierGroupForm) continue
                
                const selectedCount = modifierSkuIds.filter(skuId => {
                    const control = modifierGroupForm.get(skuId)
                    return control?.value === true
                }).length
                
                if (min !== null && selectedCount < min) {
                    errors.push(`${item.consumable.displayName} - ${modifierGroup.name}: Please select at least ${min} ${min === 1 ? 'option' : 'options'} (currently ${selectedCount} selected)`)
                }
                if (max !== null && selectedCount > max) {
                    errors.push(`${item.consumable.displayName} - ${modifierGroup.name}: Please select no more than ${max} ${max === 1 ? 'option' : 'options'} (currently ${selectedCount} selected)`)
                }
            }
        }
        
        return { valid: errors.length === 0, errors }
    }

    private validate(): boolean {
        this.submitted = true
        if (!this.bookingId || !this.docket) {
            this.cancelled.emit()
            return false
        }
        if (this.form.invalid) {
            return false
        }
        
        const courseValidation = this.validateCourseLimits()
        const modifierValidation = this.validateModifierLimits()
        
        if (!courseValidation.valid || !modifierValidation.valid) {
            const allErrors = [...courseValidation.errors, ...modifierValidation.errors]
            const errorMessage = allErrors.join('\n')
            const toast = new Toast(errorMessage)
            toast.header = 'Invalid Selection'
            toast.type = ToastType.Danger
            this.toastService.show(toast)
            return false
        }
        return true
    }

    async review() {
        if (this.saving || this.reviewModal || !this.validate()) return
        this.reviewModal = this.modalService.open(this.reviewSummary, {
            centered: true, scrollable: true, ariaLabelledBy: 'review-menu-title', size: 'lg',
        })
        const confirmed = await this.reviewModal.result.catch(() => false)
        this.reviewModal = undefined
        if (confirmed === true) this.save()
    }

    save() {
        if (this.saving || !this.validate() || !this.bookingId || !this.docket) return
        this.saving = true
        const updatedChoice = this.currentChoice
        const partySize = this.partySize || 0
        const newChoices: CoverMenuChoice[] = []
        for (let i = 0; i < partySize; i++) {
            if (i === this.coverIndex) {
                newChoices.push(updatedChoice)
            } else {
                newChoices.push(this.docket.coverChoices[i] || new CoverMenuChoice(null, null, []))
            }
        }
        const updatedDocket = new BookingDocket(
            this.docket.id,
            this.docket.menuId,
            newChoices,
            this.docket.dateCreated,
            new Date(),
            this.docket.minimumCoursesRequired,
            this.docket.maximumCoursesRequired
        )
        this.bookingService.updateBookingDocket(this.bookingId, updatedDocket).subscribe({
            next: () => {
                this.saving = false
                this.initialChoice = JSON.stringify(updatedChoice)
                const toast = new Toast('Your choices have been saved')
                toast.header = 'Menu Choices Saved'
                toast.type = ToastType.Success
                this.toastService.show(toast)
                this.saved.emit()
            },
            error: _ => {
                this.saving = false
            }
        })
    }

    isProductSelected(menuItemId: string): boolean {
        const group = this.form.get('productSelection') as FormGroup | null
        const ctrl = group?.get(menuItemId) as FormControl<boolean> | null
        return ctrl?.value === true
    }

    get isNameReadOnly(): boolean {
        return !!this.prefilledName
    }
}
